import { useEffect, useMemo, useRef } from "react";
import { useAutocompleteDebounce } from "hooks/use-autocomplete-debounce";
import {
  UNSUPPORTED_COUNTRIES,
  UNSUPPORTED_PLACE_IDS,
  HIDDEN_PLACE_IDS,
} from "settings";
import {
  type DestinationGoogleData,
  type PlaceDetails,
} from "types/quote-types";
import { googlePlacesAutocomplete } from "utils/google-places/autocomplete";
import { expireGooglePlacesSessionToken } from "utils/google-places/session-token";
import { type Destination } from "utils/google-places/destination";
import { mixpanelTrack } from "utils/tracking/mixpanel";

const MAX_FORBIDDEN_DESTINATION_INDEX = 1;
export const FORM_NAME = "destination";

export interface AutocompletePlace extends Destination {
  description: string;
  disabled?: boolean;
  error?: boolean;
  warning?: boolean;
}

function getAutocompleteResultOrder(result: AutocompletePlace) {
  return result.disabled
    ? -3
    : result.types?.includes("country")
      ? -2
      : result.terms?.at(-1)?.value.toUpperCase() !== "USA"
        ? -1
        : 0;
}

export function isDomesticTrip(places: Destination[]) {
  return places.every(({ terms }) => {
    return terms && terms[terms.length - 1]?.value.toLowerCase() === "usa";
  });
}

export function parseSelectedPlaces(
  destinations: Array<PlaceDetails | null> | null,
  destinationsGoogleData?: DestinationGoogleData[],
) {
  if (!destinations) {
    return [];
  }

  return destinations.reduce<Destination[]>((result, destination, index) => {
    if (destination) {
      result.push({
        country: destination.country,
        label: destination.label,
        placeId: destination.googleId,
        terms:
          destination.terms ?? destinationsGoogleData?.[index]?.terms ?? [],
        types:
          destination.types ?? destinationsGoogleData?.[index]?.types ?? [],
      });
    }

    return result;
  }, []);
}

export function selectedPlaceIdsAreEqual(
  a: Array<string | null>,
  b: Array<string | null>,
) {
  if (a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index++) {
    if (a[index] !== b[index]) {
      return false;
    }
  }

  return true;
}

export function useGooglePlacesAutocomplete(
  query: string,
  selectedPlaces: Destination[],
) {
  const selectedPlacesRef = useRef(selectedPlaces);
  selectedPlacesRef.current = selectedPlaces;

  const options = useAutocompleteDebounce(
    async (query) => {
      const items = await googlePlacesAutocomplete({
        input: query,
        region: null,
        types: [
          "administrative_area_level_1",
          "administrative_area_level_2",
          "country",
          "locality",
          "sublocality",
        ],
        useSessionToken: true,
      });

      if (items.length === 0) {
        return [];
      }

      const forbiddenDestinations: Record<string, string> = {};
      const result: AutocompletePlace[] = [];

      for (let index = 0; index < items.length; index++) {
        const { description, place_id: placeId, terms, types } = items[index];

        if (HIDDEN_PLACE_IDS.includes(placeId)) {
          continue;
        }

        const country = terms[terms.length - 1].value.toUpperCase();

        if (
          UNSUPPORTED_COUNTRIES.includes(country) ||
          UNSUPPORTED_PLACE_IDS.includes(placeId)
        ) {
          if (
            index <= MAX_FORBIDDEN_DESTINATION_INDEX &&
            (items.length === 1 || query.length >= 4)
          ) {
            forbiddenDestinations[`destination_${index + 1}`] = description;
            result.push({ description, disabled: true, label: "", placeId });
          }

          continue;
        }

        result.push({
          description,
          label: terms[0].value,
          placeId,
          terms,
          types,
        });
      }

      if (Object.keys(forbiddenDestinations).length) {
        forbiddenDestinations.input = query;
        mixpanelTrack("shown_forbidden_destinations", forbiddenDestinations);
      }

      result.sort(
        (a, b) => getAutocompleteResultOrder(a) - getAutocompleteResultOrder(b),
      );

      return result;
    },
    { cacheName: "faye:destinations", query },
  );

  useEffect(() => {
    return () => {
      expireGooglePlacesSessionToken();
    };
  }, []);

  return {
    options: useMemo(() => {
      return (
        options?.filter(({ placeId }) => {
          return !selectedPlacesRef.current.some(
            (selectedPlace) => selectedPlace.placeId === placeId,
          );
        }) ?? null
      );
    }, [options]),
  };
}
